/**
 * Calculates the start date (Monday) of the last completed week.
 * @returns {string} ISO date string (YYYY-MM-DD) for the Monday of the last completed week
 */
export const getLastWeekMonday = (): string => {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? 13 : day + 6;

  const lastMonday = new Date(today);
  lastMonday.setDate(today.getDate() - diff);

  const y = lastMonday.getFullYear();
  const m = String(lastMonday.getMonth() + 1).padStart(2, "0");
  const d = String(lastMonday.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
};

/**
 * Generates week metadata (week number, label, and date range) for a given start date.
 * @param {string} startDateStr - ISO date string representing the week's Monday
 * @returns {{weekNumber: number, label: string, range: string, fullLabel: string}}
 */
export const getWeekDetails = (startDateStr: string) => {
  const start = new Date(startDateStr);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  const format = (date: Date) =>
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  const firstJan = new Date(start.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(
    ((start.getTime() - firstJan.getTime()) / 86400000 + firstJan.getDay() + 1) /
      7,
  );

  return {
    weekNumber,
    label: `Week ${weekNumber}`,
    range: `${format(start)} – ${format(end)}`,
    fullLabel: `Week ${weekNumber} | ${format(start)} – ${format(end)}`,
  };
};

/**
 * Formats a date into readable full format (e.g., "April 17, 2026").
 * @param {Date} date
 * @returns {string} Localized long date string
 */
export const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

/**
 * Returns the date exactly one week before the given date.
 * @param {string} dateStr - ISO date string
 * @returns {Date} Date object representing the previous week
 */
export const getPreviousWeekDate = (dateStr: string) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() - 7);
  return d;
};

/**
 * Calculates the start date (Monday) of the next week.
 * @returns {Date} Date object representing next Monday
 */
export const getNextMonday = () => {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() + diff);

  return nextMonday;
};
